import { useState, useEffect, useCallback, useRef } from 'react';
import { LaunchesService } from '../services/launchesApi';
import type { LaunchDashboard } from '../types';

const POLL_INTERVAL_MS = 15_000;

interface UseLaunchDashboardReturn {
  data: LaunchDashboard | null;
  isLoading: boolean;
  isError: boolean;
  retry: () => void;
}

/**
 * Fetches dashboard data scoped to a single launch, and polls it like the
 * board (`useLaunchBoard`) so it reflects allocation changes made here.
 */
export function useLaunchDashboard(agencyId: string, launchId: string): UseLaunchDashboardReturn {
  const [data, setData] = useState<LaunchDashboard | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchDashboard = useCallback(() => {
    if (!agencyId || !launchId) return;
    setIsError(false);
    LaunchesService.launchDashboard(agencyId, launchId)
      .then((d) => {
        setData(d);
        setIsLoading(false);
      })
      .catch(() => {
        setIsError(true);
        setIsLoading(false);
      });
  }, [agencyId, launchId]);

  useEffect(() => {
    setIsLoading(true);
    fetchDashboard();
    intervalRef.current = setInterval(fetchDashboard, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [agencyId, launchId, fetchDashboard]);

  return { data, isLoading, isError, retry: fetchDashboard };
}
